'use client';

import { cn } from 'utils/styles';
import { useBreakpoint } from 'hooks/useBreakpoints';
import { carouselClassNames, carouselContentClassNames } from './styles';
import { BaseCarousel } from '../Carousel/BaseCarousel';
import { Colorways } from 'types/constants';
import { Margins } from 'types/constants';
import { CardGrid } from './CardGrid';
import { useEffect, useId, useState } from 'react';

interface CardCarouselProps {
  children: React.ReactNode;
  totalItems: number;
  color: Colorways;
  margins: Margins;
  sectionName?: string;
  carouselClassName?: string;
  paginationClassName?: string;
  cardGridClassName?: string;
  // Optional: portal target and class overrides for pagination
  paginationPortal?: Element | null;
  paginationPortalRef?: React.RefObject<Element | null>;
}
/**
 * CardCarousel component provides a responsive carousel layout for card elements
 * This adds a maybe unnecessary layer of abstraction over the BaseCarousel component
 * but it's necessary in order to use useBreakpoint hook without warning.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card elements to be displayed in the carousel
 * @param {number} props.totalItems - Total number of cards in the carousel
 * @param {Colorways} props.color - Color theme to apply to the carousel
 * @param {Margins} props.margins - Margin configuration for the carousel layout
 * @param {string} [props.sectionName] - Section name
 *
 * @example
 * ```tsx
 * <CardCarousel
 *   totalItems={3}
 *   color={Colorways.Primary}
 *   margins={Margins.Normal}
 *   sectionName="Featured"
 * >
 *   <Card />
 *   <Card />
 *   <Card />
 * </CardCarousel>
 * ```
 */

export function CardCarousel({
  children,
  totalItems,
  color,
  margins,
  sectionName,
  carouselClassName,
  paginationClassName,
  cardGridClassName,
  paginationPortal,
  paginationPortalRef,
}: CardCarouselProps) {
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration check, only runs once on mount
  useEffect(() => setMounted(true), []);

  const { isAboveMd } = useBreakpoint('md');
  const { isAboveLg } = useBreakpoint('lg');

  // Deterministic SSR/hydration default (this produces the least jump from design)
  const showPaginationThreshold = mounted ? (isAboveLg ? 3 : isAboveMd ? 2 : 1) : 1;

  const sharedCarouselClassName = cn(
    showPaginationThreshold > 2 ? 'justify-center align-center' : '',
    carouselClassName,
  );
  const id = useId();
  const ariaLabel = sectionName ? `Card carousel for ${sectionName}` : `Card carousel ${id}`;

  return (
    <BaseCarousel
      totalItems={totalItems}
      carouselClassName={carouselClassNames(color, sharedCarouselClassName)}
      carouselContentClassName={carouselContentClassNames(margins)}
      carouselPaginationClassName={cn('mt-12', paginationClassName)}
      aria-label={ariaLabel}
      aria-roledescription={ariaLabel}
      showPaginationThreshold={showPaginationThreshold}
      paginationPortal={paginationPortal}
      paginationPortalRef={paginationPortalRef}>
      <CardGrid className={cn('lg:auto-cols-[32%]', cardGridClassName)}>{children}</CardGrid>
    </BaseCarousel>
  );
}
